import React, { useContext, useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { useLocation, useNavigate } from "react-router-dom";
import { ThemeContext } from "../../hooks/ThemeContext";
import { UserContext } from "../../hooks/UserContext";
import { CartContext } from "../../hooks/CartContext";
import { Moon, Sun, Mail } from "lucide-react";
import FranceFlag from "../../assets/images/fr.png"; // Import de l'image PNG
import UKFlag from "../../assets/images/uk.png"; // Import de l'image PNG
import {
  FaShoppingCart,
  FaBars,
  FaTimes,
  FaChevronDown,
  FaUser,
  FaSignOutAlt,
} from "react-icons/fa";
import { useTranslation } from "react-i18next"; // Importation du hook i18n
import { api } from "../../services/baseUrl";

const Navbar = () => {
  const { user, isAuthenticated, role } = useContext(UserContext);
  const { cart } = useContext(CartContext);
  const { theme, toggleTheme, setLanguage } = useContext(ThemeContext);
  const { t, i18n } = useTranslation(); // Utilisation du hook pour la traduction
  const location = useLocation();
  const navigate = useNavigate();
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [userMenuOpen, setUserMenuOpen] = useState(false);
  const [unreadCount, setUnreadCount] = useState(0);
  const [hasActiveProject, setHasActiveProject] = useState(false);

  const toggleDrawer = () => setDrawerOpen(!drawerOpen);

  const toggleUserMenu = () => {
    setUserMenuOpen(!userMenuOpen);
    setDropdownOpen(false);
  };

  const handleLanguageChange = (lang) => {
    localStorage.setItem("language", lang);
    i18n.changeLanguage(lang);
    setLanguage(lang);
  };

  const closeMenus = () => {
    setDropdownOpen(false);
    setUserMenuOpen(false);
  };

  const handleLinkClick = () => {
    closeMenus();
  };

  const logout = () => {
    localStorage.removeItem("access_token");
    window.location.reload();
  };

  // Fermer les dropdowns en cliquant en dehors
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        !event.target.closest(".dropdown-container") &&
        !event.target.closest(".user-menu-container")
      ) {
        closeMenus();
      }
    };

    if (dropdownOpen || userMenuOpen) {
      document.addEventListener("mousedown", handleClickOutside);
      return () =>
        document.removeEventListener("mousedown", handleClickOutside);
    }
  }, [dropdownOpen, userMenuOpen]);

  // Charger le nombre de messages non lus
  useEffect(() => {
    if (isAuthenticated) {
      fetchUnreadCount();
      const interval = setInterval(fetchUnreadCount, 30000); // Refresh toutes les 30s
      return () => clearInterval(interval);
    }
  }, [isAuthenticated]);

  // Vérifier si l'utilisateur a un projet actif
  useEffect(() => {
    if (isAuthenticated && role === 1) {
      api
        .get("/projects/my")
        .then((res) => {
          const projs = res.data?.projects || [];
          setHasActiveProject(projs.length > 0);
        })
        .catch(() => setHasActiveProject(false));
    } else {
      setHasActiveProject(false);
    }
  }, [isAuthenticated, role]);

  const fetchUnreadCount = async () => {
    try {
      const response = await api.get("/conversations/unread-count");
      setUnreadCount(response.data.unread_count || 0);
    } catch (error) {
      console.error("Erreur chargement messages:", error);
      setUnreadCount(0);
    }
  };

  const isDark = theme === "dark";
  const gPrimary = {
    backgroundImage:
      "linear-gradient(135deg, #f43f5e 0%, #a855f7 50%, #6366f1 100%)",
  };

  return (
    <nav
      className={`sticky top-0 z-50 border-b transition-colors ${
        isDark
          ? "border-white/[0.06] text-white"
          : "bg-white border-gray-100 text-gray-900"
      }`}
      style={{ backgroundColor: isDark ? "#07070a" : undefined }}
    >
      <div className="max-w-7xl mx-auto px-6 lg:px-10 h-16 flex items-center justify-between gap-6">
        {/* Logo */}
        <Link to="/" className="flex items-center gap-2.5 flex-shrink-0">
          <span
            className="h-2 w-2 rounded-full flex-shrink-0"
            style={{ background: "linear-gradient(135deg, #f43f5e, #a855f7)" }}
          />
          <span
            className="text-base font-black"
            style={{ letterSpacing: "-0.04em" }}
          >
            KRAYNE{" "}
            <span className="bg-clip-text text-transparent" style={gPrimary}>
              AGENCY
            </span>
          </span>
        </Link>

        {/* Desktop nav */}
        <ul className="hidden lg:flex items-center gap-1 flex-1">
          {[
            ["/", "Accueil"],
            ["/services", "Services"],
            ["/portfolio", "Réalisations"],
            ["/tarifs", "Tarifs"],
            ["/contact", "Contact"],
          ].map(([path, label]) => {
            const active = location.pathname === path;
            return (
              <li key={path}>
                <Link
                  to={path}
                  onClick={handleLinkClick}
                  className={`px-4 py-2 rounded-xl text-sm font-medium transition-all duration-200 ${
                    active
                      ? isDark
                        ? "bg-white/[0.07] text-white"
                        : "bg-gray-100 text-gray-900"
                      : isDark
                        ? "text-gray-400 hover:text-white hover:bg-white/[0.04]"
                        : "text-gray-500 hover:text-gray-900 hover:bg-gray-50"
                  }`}
                >
                  {label}
                </Link>
              </li>
            );
          })}
          {isAuthenticated && role === 1 && hasActiveProject && (
            <li>
              <Link
                to="/espace-client?tab=projects"
                onClick={handleLinkClick}
                className={`px-4 py-2 rounded-xl text-sm font-medium transition-all duration-200 ${
                  isDark
                    ? "text-violet-400 hover:text-violet-300 hover:bg-violet-500/[0.08]"
                    : "text-violet-600 hover:text-violet-700 hover:bg-violet-50"
                }`}
              >
                Mon projet
              </Link>
            </li>
          )}
          {role === 3 && (
            <li>
              <Link
                to="/dashboard"
                onClick={handleLinkClick}
                className={`px-4 py-2 rounded-xl text-sm font-medium transition-all duration-200 ${
                  isDark
                    ? "text-rose-400 hover:text-rose-300 hover:bg-rose-500/[0.08]"
                    : "text-rose-600 hover:text-rose-700 hover:bg-rose-50"
                }`}
              >
                Admin
              </Link>
            </li>
          )}
        </ul>

        {/* Actions */}
        <div className="flex items-center gap-1.5">
          {/* Theme toggle */}
          <button
            onClick={() => toggleTheme()}
            aria-label="Toggle theme"
            className={`h-9 w-9 flex items-center justify-center rounded-xl transition-all ${
              isDark
                ? "text-gray-400 hover:text-white hover:bg-white/[0.06]"
                : "text-gray-500 hover:text-gray-900 hover:bg-gray-100"
            }`}
          >
            {isDark ? (
              <Sun className="w-4 h-4" />
            ) : (
              <Moon className="w-4 h-4" />
            )}
          </button>

          {/* Cart */}
          <Link
            to="/cart"
            className={`relative h-9 px-3 flex items-center gap-2 rounded-xl text-sm font-medium transition-all ${
              isDark
                ? "text-gray-400 hover:text-white hover:bg-white/[0.06]"
                : "text-gray-500 hover:text-gray-900 hover:bg-gray-100"
            }`}
          >
            <FaShoppingCart className="w-4 h-4" />
            <span className="hidden sm:block">{t("cart")}</span>
            {cart.items_count > 0 && (
              <span
                className="absolute -top-1 -right-1 h-4 w-4 flex items-center justify-center rounded-full text-[10px] font-black text-white"
                style={{
                  background: "linear-gradient(135deg, #f43f5e, #a855f7)",
                }}
              >
                {cart.items_count}
              </span>
            )}
          </Link>

          {/* CTA connexion */}
          {!isAuthenticated && (
            <Link
              to="/login"
              className="inline-flex items-center gap-2 px-4 py-2 rounded-xl text-sm font-bold text-white transition-all hover:-translate-y-0.5"
              style={{
                background:
                  "linear-gradient(135deg, #f43f5e, #a855f7, #6366f1)",
              }}
            >
              Se connecter
            </Link>
          )}

          {/* User dropdown */}
          {isAuthenticated && (
            <div className="relative user-menu-container">
              <button
                onClick={toggleUserMenu}
                className={`flex items-center gap-2 pl-2 pr-3 py-1.5 rounded-xl transition-all ${
                  isDark ? "hover:bg-white/[0.06]" : "hover:bg-gray-100"
                }`}
              >
                <div
                  className="w-7 h-7 rounded-full flex items-center justify-center text-xs font-black text-white flex-shrink-0"
                  style={{
                    background: "linear-gradient(135deg, #f43f5e, #a855f7)",
                  }}
                >
                  {(user?.nickname || user?.name || "U")[0].toUpperCase()}
                </div>
                <span className="hidden md:block text-sm font-semibold leading-none">
                  {user?.nickname || user?.name}
                </span>
                <FaChevronDown
                  className={`text-[10px] transition-transform ${userMenuOpen ? "rotate-180" : ""} ${
                    isDark ? "text-gray-500" : "text-gray-400"
                  }`}
                />
              </button>

              {userMenuOpen && (
                <div
                  className={`absolute right-0 top-full mt-2 w-60 rounded-2xl shadow-2xl border overflow-hidden z-50 ${isDark ? "border-white/[0.10]" : "border-gray-200"}`}
                  style={{ backgroundColor: isDark ? "#0f0f14" : "#ffffff" }}
                >
                  <div
                    className={`px-4 py-3 border-b ${isDark ? "border-white/[0.06]" : "border-gray-100"}`}
                  >
                    <p
                      className={`text-sm font-bold ${isDark ? "text-white" : "text-gray-900"}`}
                    >
                      {user?.nickname || user?.name}
                    </p>
                    <p className="text-xs mt-0.5 text-gray-500">
                      {user?.email}
                    </p>
                  </div>
                  <div className="py-2">
                    <Link
                      to={role === 3 ? "/dashboard" : "/espace-client"}
                      onClick={closeMenus}
                      className={`flex items-center gap-3 px-4 py-2.5 text-sm transition-colors ${isDark ? "hover:bg-white/[0.04] text-gray-300" : "hover:bg-gray-50 text-gray-700"}`}
                    >
                      <FaUser
                        className={`w-3.5 h-3.5 ${role === 3 ? "text-rose-400" : "text-violet-400"}`}
                      />
                      {role === 3 ? "Tableau de bord" : "Espace client"}
                    </Link>
                    {role === 1 && hasActiveProject && (
                      <Link
                        to="/espace-client?tab=projects"
                        onClick={closeMenus}
                        className={`flex items-center gap-3 px-4 py-2.5 text-sm transition-colors ${isDark ? "hover:bg-white/[0.04] text-gray-300" : "hover:bg-gray-50 text-gray-700"}`}
                      >
                        <span>📋</span>
                        Suivi de projet
                      </Link>
                    )}
                    {role !== 3 && (
                      <Link
                        to="/espace-client?tab=messages"
                        onClick={closeMenus}
                        className={`flex items-center justify-between px-4 py-2.5 text-sm transition-colors ${isDark ? "hover:bg-white/[0.04] text-gray-300" : "hover:bg-gray-50 text-gray-700"}`}
                      >
                        <div className="flex items-center gap-3">
                          <Mail className="w-3.5 h-3.5 text-violet-400" />
                          Messages
                        </div>
                        {unreadCount > 0 && (
                          <span
                            className="px-2 py-0.5 rounded-full text-[10px] font-black text-white"
                            style={{
                              background:
                                "linear-gradient(135deg, #f43f5e, #a855f7)",
                            }}
                          >
                            {unreadCount}
                          </span>
                        )}
                      </Link>
                    )}
                    {role === 3 && (
                      <Link
                        to="/monia"
                        onClick={closeMenus}
                        className={`flex items-center gap-3 px-4 py-2.5 text-sm transition-colors ${isDark ? "hover:bg-white/[0.04] text-gray-300" : "hover:bg-gray-50 text-gray-700"}`}
                      >
                        <span>🤖</span>
                        Monia (IA)
                      </Link>
                    )}
                  </div>
                  <div
                    className={`border-t ${isDark ? "border-white/[0.06]" : "border-gray-100"} pt-1 pb-2`}
                  >
                    <button
                      onClick={() => {
                        logout();
                        closeMenus();
                      }}
                      className="w-full flex items-center gap-3 px-4 py-2.5 text-sm text-rose-500 hover:bg-rose-500/[0.08] transition-colors font-semibold"
                    >
                      <FaSignOutAlt />
                      Déconnexion
                    </button>
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Hamburger mobile */}
          <button
            onClick={toggleDrawer}
            className={`lg:hidden h-9 w-9 flex items-center justify-center rounded-xl transition-all ml-1 ${
              isDark
                ? "text-gray-400 hover:text-white hover:bg-white/[0.06]"
                : "text-gray-500 hover:text-gray-900 hover:bg-gray-100"
            }`}
            aria-label="Menu"
          >
            {drawerOpen ? <FaTimes /> : <FaBars />}
          </button>
        </div>
      </div>

      {/* Mobile drawer */}
      <div className="lg:hidden fixed inset-0 z-40 pointer-events-none">
        <div
          className={`absolute inset-0 transition-opacity duration-300 bg-black/60 backdrop-blur-sm ${
            drawerOpen ? "opacity-100 pointer-events-auto" : "opacity-0"
          }`}
          onClick={toggleDrawer}
        />
        <aside
          className={`absolute left-0 top-0 w-72 h-full flex flex-col transform transition-transform duration-300 ${
            isDark ? "bg-[#07070a] text-white" : "bg-white text-gray-900"
          } ${
            drawerOpen
              ? "translate-x-0 pointer-events-auto"
              : "-translate-x-full pointer-events-none"
          } shadow-2xl`}
        >
          <div
            className={`flex items-center justify-between px-6 h-16 flex-shrink-0 border-b ${isDark ? "border-white/[0.06]" : "border-gray-100"}`}
          >
            <Link
              to="/"
              onClick={toggleDrawer}
              className="flex items-center gap-2.5"
            >
              <span
                className="h-2 w-2 rounded-full flex-shrink-0"
                style={{
                  background: "linear-gradient(135deg, #f43f5e, #a855f7)",
                }}
              />
              <span
                className="font-black text-base"
                style={{ letterSpacing: "-0.04em" }}
              >
                KRAYNE{" "}
                <span
                  className="bg-clip-text text-transparent"
                  style={gPrimary}
                >
                  AGENCY
                </span>
              </span>
            </Link>
            <button
              onClick={toggleDrawer}
              className={`h-8 w-8 flex items-center justify-center rounded-lg ${isDark ? "hover:bg-white/[0.06]" : "hover:bg-gray-100"}`}
            >
              <FaTimes />
            </button>
          </div>

          <nav className="flex-1 overflow-y-auto px-4 py-6 flex flex-col gap-1">
            {[
              ["/", "Accueil"],
              ["/services", "Services"],
              ["/portfolio", "Réalisations"],
              ["/tarifs", "Tarifs"],
              ["/contact", "Contact"],
            ].map(([path, label]) => {
              const active = location.pathname === path;
              return (
                <Link
                  key={path}
                  to={path}
                  onClick={() => {
                    navigate(path);
                    toggleDrawer();
                  }}
                  className={`px-4 py-3 rounded-xl text-sm font-medium transition-all ${
                    active
                      ? isDark
                        ? "bg-white/[0.07] text-white"
                        : "bg-gray-100 text-gray-900"
                      : isDark
                        ? "text-gray-400 hover:text-white hover:bg-white/[0.04]"
                        : "text-gray-600 hover:text-gray-900 hover:bg-gray-50"
                  }`}
                >
                  {label}
                </Link>
              );
            })}
            {role === 3 && (
              <Link
                to="/dashboard"
                onClick={() => {
                  navigate("/dashboard");
                  toggleDrawer();
                }}
                className={`px-4 py-3 rounded-xl text-sm font-medium transition-all ${
                  isDark
                    ? "text-rose-400 hover:bg-white/[0.04]"
                    : "text-rose-600 hover:bg-gray-50"
                }`}
              >
                Admin
              </Link>
            )}
          </nav>

          <div
            className={`px-4 py-6 flex-shrink-0 border-t ${isDark ? "border-white/[0.06]" : "border-gray-100"}`}
          >
            {!isAuthenticated ? (
              <Link
                to="/login"
                onClick={toggleDrawer}
                className="flex items-center justify-center w-full py-3 rounded-xl text-sm font-bold text-white"
                style={{
                  background:
                    "linear-gradient(135deg, #f43f5e, #a855f7, #6366f1)",
                }}
              >
                Se connecter
              </Link>
            ) : (
              <button
                onClick={() => {
                  logout();
                  toggleDrawer();
                }}
                className="w-full flex items-center justify-center gap-2 py-3 rounded-xl text-sm font-semibold text-rose-500 border border-rose-500/20 hover:bg-rose-500/[0.08] transition-colors"
              >
                <FaSignOutAlt />
                Déconnexion
              </button>
            )}
          </div>
        </aside>
      </div>
    </nav>
  );
};

export default Navbar;
